import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { isEqual } from 'lodash-es';
import { Save, ExternalLink } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import type { TypeEngineAgent } from '@intone/shared/types';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { useVoicesList } from '@/entities/voice';
import { useLLMsList, getPath } from '@/entities/llm';

import { STTConfigurationBlock, type STTConfigurationBlockRef } from '@/entities/sttConfiguration';
import { TurnDetectionConfigurationBlock, type TurnDetectionConfigurationBlockRef } from '@/entities/turnDetectionConfiguration';
import { TimeoutConfigurationBlock, type TimeoutConfigurationBlockRef } from '@/entities/timeoutConfiguration';

import { useUpdateEngineAgent, useEngineAgentsList } from '@/entities/engineAgent';
import { useShowDialog } from '@/hooks/useShowDialog';
import { PageHeader } from './PageHeader';

const DONT_COPY = '__own__';
const COPIED = '__copied__';

type ConfigType = 'stt' | 'td' | 'timeout';

const CONFIG_FIELDS = {
  stt: { id: 'stt_config_id', copied: 'stt_config_copied' },
  td: { id: 'turn_detection_config_id', copied: 'turn_detection_config_copied' },
  timeout: { id: 'timeout_config_id', copied: 'timeout_config_copied' },
} as const;

type AgentEditorPageProps = {
  agent: TypeEngineAgent;
  agentId: string;
  hideHeader?: boolean;
};

export function AgentForm(params: AgentEditorPageProps) {
  const { agentId, agent, hideHeader } = params;
  const isTextCampaign = agent.campaign_telephony === 'custom';

  const { data: voices = [] } = useVoicesList();
  const { data: llms = [] } = useLLMsList();
  const { data: agentsList = [] } = useEngineAgentsList();
  const saveAgent = useUpdateEngineAgent();

  const otherAgents = useMemo(
    () => agentsList.filter(a => a.alt_key !== agentId),
    [agentsList, agentId]
  );

  const [currentAgent, setCurrentAgent] = useState<TypeEngineAgent>(agent);
  const [sttHasChanges, setSttHasChanges] = useState(false);
  const [turnDetectionHasChanges, setTurnDetectionHasChanges] = useState(false);
  const [timeoutHasChanges, setTimeoutHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const initCopySource = useCallback((type: ConfigType): string => {
    const copiedFlag = agent[CONFIG_FIELDS[type].copied];
    if (!copiedFlag) return DONT_COPY;
    const configId = agent[CONFIG_FIELDS[type].id];
    const sourceAgent = otherAgents.find(a => a[CONFIG_FIELDS[type].id] === configId);
    return sourceAgent?.alt_key ?? COPIED;
  }, [agent, otherAgents]);

  const [sttSource, setSttSource] = useState<string>(DONT_COPY);
  const [tdSource, setTdSource] = useState<string>(DONT_COPY);
  const [timeoutSource, setTimeoutSource] = useState<string>(DONT_COPY);

  useEffect(() => {
    if (otherAgents.length > 0) {
      setSttSource(initCopySource('stt'));
      setTdSource(initCopySource('td'));
      setTimeoutSource(initCopySource('timeout'));
    }
  }, [agent, otherAgents, initCopySource]);

  const sttRef = useRef<STTConfigurationBlockRef>(null);
  const turnDetectionRef = useRef<TurnDetectionConfigurationBlockRef>(null);
  const timeoutRef = useRef<TimeoutConfigurationBlockRef>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    setCurrentAgent(agent);
  }, [agent]);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const handleConfigSourceChange = useCallback((
    type: ConfigType,
    sourceAltKey: string,
  ) => {
    const setter = type === 'stt' ? setSttSource : type === 'td' ? setTdSource : setTimeoutSource;
    setter(sourceAltKey);

    const { id: idField, copied: copiedField } = CONFIG_FIELDS[type];

    if (sourceAltKey === DONT_COPY) {
      setCurrentAgent(prev => ({
        ...prev,
        [copiedField]: false,
        [idField]: agent[idField],
      }));
    } else {
      const source = otherAgents.find(a => a.alt_key === sourceAltKey);
      if (!source) return;
      setCurrentAgent(prev => ({
        ...prev,
        [copiedField]: true,
        [idField]: source[idField],
      }));
    }
  }, [agent, otherAgents]);

  const renderConfigSourceSelect = useCallback((
    type: ConfigType,
    value: string,
  ) => {
    const displayValue = value === COPIED ? COPIED : value;
    return (
      <div className="flex items-center gap-2">
        <span className="text-detail text-gray-500 whitespace-nowrap">Use config from:</span>
        <Select value={displayValue} onValueChange={(v) => handleConfigSourceChange(type, v)}>
          <SelectTrigger className="h-7 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DONT_COPY}>Don&apos;t copy</SelectItem>
            {otherAgents.map(a => (
              <SelectItem key={a.alt_key} value={a.alt_key}>
                {a.name || 'Unnamed'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }, [otherAgents, handleConfigSourceChange]);

  const basicInfoHasChanges = useMemo(() => !isEqual(currentAgent, agent), [currentAgent, agent]);

  const hasAnyChanges = settled && (basicInfoHasChanges || sttHasChanges || turnDetectionHasChanges || timeoutHasChanges);

  useShowDialog(hasAnyChanges);

  const updateAgent = (field: keyof TypeEngineAgent, value: any) => {
    setCurrentAgent((prev: TypeEngineAgent) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveAll = useCallback(async () => {
    if (!hasAnyChanges) return;

    setIsSaving(true);
    let allSuccess = true;

    try {
      if (basicInfoHasChanges) {
        if (!currentAgent.name) {
          toast.error('Agent name is required');
          setIsSaving(false);
          return;
        }

        const { alt_key, ...data } = currentAgent;
        await new Promise<void>((resolve, reject) => {
          saveAgent.mutate(
            { agentId, ...data },
            {
              onSuccess: () => resolve(),
              onError: (error: any) => {
                toast.error(error.message || 'Failed to update agent');
                reject(error);
              },
            }
          );
        });
      }

      const savePromises: Promise<boolean>[] = [];

      if (sttRef.current?.hasChanges()) {
        savePromises.push(sttRef.current.save());
      }
      if (turnDetectionRef.current?.hasChanges()) {
        savePromises.push(turnDetectionRef.current.save());
      }
      if (timeoutRef.current?.hasChanges()) {
        savePromises.push(timeoutRef.current.save());
      }

      if (savePromises.length > 0) {
        const results = await Promise.all(savePromises);
        allSuccess = results.every((r) => r);
      }

      if (allSuccess) {
        toast.success('All changes saved successfully');
      }
    } catch {
      allSuccess = false;
    } finally {
      setIsSaving(false);
    }
  }, [hasAnyChanges, basicInfoHasChanges, currentAgent, agentId, saveAgent]);

  return (
    <div className={`w-full h-full overflow-y-auto ${hideHeader ? 'px-4 pb-4' : 'p-4'}`}>
      {!hideHeader && <PageHeader />}

      <div className="space-y-3">
        <Card className="border-gray-300">
          <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-body font-semibold">Agent Settings</CardTitle>
              <Button
                type="button"
                variant="default"
                size="sm"
                disabled={!hasAnyChanges || isSaving}
                onClick={handleSaveAll}
              >
                {isSaving ? (
                  'Saving...'
                ) : (
                  <>
                    <Save className="w-3 h-3 mr-2" />
                    Save All Changes
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="agent-name" className="font-medium text-gray-900">
                Agent Name
              </Label>
              <Input
                id="agent-name"
                value={currentAgent.name || ''}
                onChange={(e) => updateAgent('name', e.target.value)}
                placeholder="e.g., Rachel"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="experiment-tag" className="font-medium text-gray-900">
                Experiment Tag (Optional)
              </Label>
              <Input
                id="experiment-tag"
                value={currentAgent.experiment_tag || undefined}
                onChange={(e) => updateAgent('experiment_tag', e.target.value)}
                placeholder="e.g., A/B test variant"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="llm-select" className="font-medium text-gray-900">
                LLM
              </Label>
              <div className="flex gap-2 items-center">
                <Select
                  value={currentAgent.llm_id?.toString() || ''}
                  onValueChange={(value) => updateAgent('llm_id', value ? parseInt(value) : null)}
                >
                  <SelectTrigger id="llm-select" className="h-8">
                    <SelectValue placeholder="Select LLM" />
                  </SelectTrigger>
                  <SelectContent>
                    {llms.map((llm) => (
                      <SelectItem key={llm.id} value={llm.id.toString()}>
                        <span className="truncate block text-left">
                          {llm.name || 'Unnamed'} - {llm.model}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currentAgent.llm_id && (
                  <Link
                    to={getPath(currentAgent.llm_id.toString())}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-7 w-7 p-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md text-detail"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </div>

            {!isTextCampaign && (
              <div className="grid gap-2">
                <Label htmlFor="voice-select" className="font-medium text-gray-900">
                  Voice
                </Label>
                <Select
                  value={currentAgent.voice_id?.toString() || ''}
                  onValueChange={(value) => updateAgent('voice_id', value ? parseInt(value) : null)}
                >
                  <SelectTrigger id="voice-select" className="h-8">
                    <SelectValue placeholder="Select Voice" />
                  </SelectTrigger>
                  <SelectContent>
                    {voices.map((voice) => (
                      <SelectItem key={voice.id} value={voice.id.toString()}>
                        <span className="truncate block text-left">
                          {voice.name} ({voice.alt_key})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {isTextCampaign && (
          <Card className="border-gray-300">
            <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
              <CardTitle className="text-body font-semibold">Text Session Configuration</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="timeout-minutes" className="font-medium text-gray-900">
                  End Session Timeout (minutes)
                </Label>
                <Input
                  id="timeout-minutes"
                  type="number"
                  min={1}
                  max={1440}
                  value={currentAgent.text_session?.timeout_minutes ?? ''}
                  onChange={(e) => updateAgent('text_session', {
                    ...currentAgent.text_session,
                    timeout_minutes: parseInt(e.target.value) || 0,
                    timeout_message: currentAgent.text_session?.timeout_message ?? '',
                  })}
                  placeholder="e.g., 5"
                />
              </div>
              <div className="grid gap-2 md:col-span-2">
                <Label htmlFor="timeout-message" className="font-medium text-gray-900">
                  End Session Timeout Message
                </Label>
                <Textarea
                  id="timeout-message"
                  value={currentAgent.text_session?.timeout_message ?? ''}
                  onChange={(e) => updateAgent('text_session', {
                    ...currentAgent.text_session,
                    timeout_minutes: currentAgent.text_session?.timeout_minutes ?? 0,
                    timeout_message: e.target.value,
                  })}
                  placeholder="Session ended due to inactivity."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {!isTextCampaign && currentAgent.stt_config_id && (
          <STTConfigurationBlock
            ref={sttRef}
            agentId={agentId}
            selectedId={currentAgent.stt_config_id}
            onHasChanges={setSttHasChanges}
            readOnly={sttSource !== DONT_COPY}
            headerRight={renderConfigSourceSelect('stt', sttSource)}
          />
        )}

        {!isTextCampaign && currentAgent.turn_detection_config_id && (
          <TurnDetectionConfigurationBlock
            ref={turnDetectionRef}
            agentId={agentId}
            selectedId={currentAgent.turn_detection_config_id}
            onHasChanges={setTurnDetectionHasChanges}
            readOnly={tdSource !== DONT_COPY}
            headerRight={renderConfigSourceSelect('td', tdSource)}
          />
        )}

        {!isTextCampaign && currentAgent.timeout_config_id && (
          <TimeoutConfigurationBlock
            ref={timeoutRef}
            agentId={agentId}
            selectedId={currentAgent.timeout_config_id}
            onHasChanges={setTimeoutHasChanges}
            readOnly={timeoutSource !== DONT_COPY}
            headerRight={renderConfigSourceSelect('timeout', timeoutSource)}
          />
        )}
      </div>
    </div>
  );
}